export function getNumericAuthUserId(
  user: { id?: unknown } | null | undefined,
): number | undefined {
  const raw = user?.id;
  if (raw == null || !Number.isFinite(Number(raw)) || Number(raw) <= 0) {
    return undefined;
  }
  return Number(raw);
}

export type TeacherProfileLookupRow = {
  email?: string | null;
  /** OpenAPI ids are not always typed as `number` at compile time. */
  teacherId?: unknown;
};

export type UserEmailLookupRow = {
  userId?: unknown;
  email?: string | null;
};

/** Map teacher email → profile `teacherId` (appointments / special hours APIs). */
export function resolveTeacherProfileIdFromEmail(
  email: string | null | undefined,
  teachers: TeacherProfileLookupRow[] | undefined,
): number | undefined {
  if (!email?.trim() || !teachers?.length) {
    return undefined;
  }

  const normalized = email.trim().toLowerCase();
  const match = teachers.find(
    (row) => row.email?.trim().toLowerCase() === normalized,
  );

  if (match?.teacherId != null && Number(match.teacherId) > 0) {
    return Number(match.teacherId);
  }

  return undefined;
}

/**
 * Resolve auth `userId` → teacher profile id for appointment APIs.
 * Returns null when email cannot be matched (Time Clock private-lesson linking).
 */
export function resolveTeacherProfileIdForUser(
  targetUserId: number,
  context: {
    users: UserEmailLookupRow[];
    teachers: TeacherProfileLookupRow[] | undefined;
    currentUserId?: number;
    currentUserEmail?: string | null;
  },
): number | null {
  let email: string | null | undefined;
  const matchedUser = context.users.find(
    (row) => Number(row.userId) === targetUserId,
  );

  if (matchedUser?.email) {
    email = matchedUser.email;
  } else if (
    context.currentUserId != null &&
    targetUserId === context.currentUserId &&
    context.currentUserEmail
  ) {
    email = context.currentUserEmail;
  }

  const profileId = resolveTeacherProfileIdFromEmail(email, context.teachers);
  return profileId ?? null;
}

/** Map teacher email → auth `userId` (user-tasks APIs use Users.Id, not teacher profile id). */
export function resolveUserIdFromTeacherEmail(
  email: string | null | undefined,
  users: UserEmailLookupRow[] | undefined,
): number | undefined {
  if (!email?.trim() || !users?.length) {
    return undefined;
  }

  const normalized = email.trim().toLowerCase();
  const match = users.find(
    (row) => row.email?.trim().toLowerCase() === normalized,
  );

  if (match?.userId != null && Number(match.userId) > 0) {
    return Number(match.userId);
  }

  return undefined;
}
